import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import MobileShell from './components/v2/layout/MobileShell';
import { ThemeProvider } from './lib/v2/ThemeProvider';
import LandingPage from './components/LandingPage';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import VerifyEmailPage from './components/auth/VerifyEmailPage';
import McpConnectPage from './components/auth/McpConnectPage';
import AppEmailVerificationGate from './components/auth/AppEmailVerificationGate';
import ImproveModelConsentGate from './components/auth/ImproveModelConsentGate';
import CookieConsentBanner from './components/CookieConsentBanner';
import SupportPage from './components/public/SupportPage';
import { PrivacyRoutePage, TermsRoutePage } from './components/public/StaticLegalRedirect';
import { AppView } from './types';
import { captureAttributionFromUrl } from './utils/attribution';
import { getCurrentUserToken, auth, signOutUser } from './utils/firebase';

const getInitialPage = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get('page');
};

const USE_CREATE_VIEW_V2 = true;

/** Soft-reload once when a stale Vite chunk 404s after deploy (PIXLIE-REACT-C). */
function lazyWithChunkReload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const isChunk =
        /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
          msg,
        );
      const key = 'pixlie_chunk_reload';
      if (isChunk && typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    }
  });
}

const CreateView = lazyWithChunkReload(() =>
  USE_CREATE_VIEW_V2
    ? import('./components/views/CreateViewV2')
    : import('./components/views/CreateView'),
);
const DashboardView = lazyWithChunkReload(
  () => import('./components/v2/Dashboard/DashboardViewV2'),
);
const UsageView = lazyWithChunkReload(() => import('./components/views/UsageView'));
const BillingView = lazyWithChunkReload(() => import('./components/views/BillingView'));
const EditorView = lazyWithChunkReload(() => import('./components/views/EditorView'));
const LibraryView = lazyWithChunkReload(() => import('./components/views/LibraryView'));
const WorkflowsView = lazyWithChunkReload(() => import('./components/views/WorkflowsView'));
const SettingsView = lazyWithChunkReload(() => import('./components/views/SettingsView'));
const ProfileView = lazyWithChunkReload(() => import('./components/views/ProfileView'));

const ViewLoader = () => (
  <div className="flex h-full w-full items-center justify-center">
    <div className="animate-pulse text-textSecondary">Loading...</div>
  </div>
);

const ProtectedRoute: React.FC = () => {
  const location = useLocation();
  const [visualPreview] = useState(
    () => import.meta.env.DEV && new URLSearchParams(location.search).get('visual') === '1',
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(() => visualPreview ? true : null);
  const navigate = useNavigate();

  useEffect(() => {
    if (visualPreview) {
      setIsAuthenticated(true);
      return undefined;
    }
    let cancelled = false;

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (cancelled) return;
      setIsAuthenticated(!!user);
      if (!user) {
        navigate('/login', { replace: true });
      }
    });

    void (async () => {
      try {
        const token = await getCurrentUserToken();
        if (cancelled) return;
        if (token && auth.currentUser) {
          setIsAuthenticated(true);
          return;
        }
        if (!auth.currentUser) {
          setIsAuthenticated(false);
          navigate('/login', { replace: true });
        }
      } catch {
        if (!cancelled && !auth.currentUser) {
          setIsAuthenticated(false);
          navigate('/login', { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [navigate, visualPreview]);

  if (isAuthenticated === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="animate-pulse text-textSecondary">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <Outlet />;
};

const pathToView = (path: string): AppView => {
  if (path === '/dashboard' || path === '/') return AppView.HOME;
  if (path === '/create') return AppView.CREATE;
  if (path === '/library') return AppView.LIBRARY;
  if (path === '/billing') return AppView.BILLING;
  if (path === '/settings') return AppView.SETTINGS;
  if (path === '/profile') return AppView.PROFILE;
  if (path === '/editor') return AppView.EDITOR;
  if (path === '/workflows') return AppView.WORKFLOWS;
  if (path === '/usage') return AppView.USAGE;
  return AppView.HOME;
};

const viewToPath = (view: AppView): string => {
  switch (view) {
    case AppView.CREATE:
      return '/create';
    case AppView.LIBRARY:
      return '/library';
    case AppView.BILLING:
      return '/billing';
    case AppView.SETTINGS:
      return '/settings';
    case AppView.PROFILE:
      return '/profile';
    case AppView.EDITOR:
      return '/editor';
    case AppView.WORKFLOWS:
      return '/workflows';
    case AppView.USAGE:
      return '/usage';
    case AppView.HOME:
    default:
      return '/dashboard';
  }
};

const DashboardRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<ViewLoader />}>
      <DashboardView onNavigate={(view) => navigate(viewToPath(view))} />
    </Suspense>
  );
};

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const AppLayout: React.FC = () => {
  const location = useLocation();
  const [currentView, setCurrentView] = useState<AppView>(() => pathToView(location.pathname));
  const [userEmail, setUserEmail] = useState<string | null>(() => auth.currentUser?.email ?? null);
  const [userDisplayName, setUserDisplayName] = useState<string | null>(
    () => auth.currentUser?.displayName ?? null,
  );
  const [planName, setPlanName] = useState('Free');

  useEffect(() => {
    setCurrentView(pathToView(location.pathname));
  }, [location.pathname]);

  useEffect(
    () =>
      auth.onAuthStateChanged((user) => {
        setUserEmail(user?.email ?? null);
        setUserDisplayName(user?.displayName ?? null);
      }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const loadPlan = async () => {
      const user = auth.currentUser;
      if (!user?.uid) {
        if (!cancelled) setPlanName('Free');
        return;
      }
      try {
        const token = await getCurrentUserToken();
        if (!token) return;
        const res = await fetch(`${BACKEND_URL}/api/billing/subscription/${user.uid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as { plan?: string };
        if (!cancelled) setPlanName(data.plan || 'free');
      } catch {
        /* keep last known / Free */
      }
    };
    void loadPlan();
    return () => {
      cancelled = true;
    };
  }, [userEmail]);

  const handleNavigate = (view: AppView) => {
    setCurrentView(view);
  };

  return (
    <MobileShell
      currentView={currentView}
      onChangeView={handleNavigate}
      userEmail={userEmail}
      userDisplayName={userDisplayName}
      planName={planName}
      onSignOut={signOutUser}
    >
      <ImproveModelConsentGate planTier="free" />
      <CookieConsentBanner />
      <Outlet />
    </MobileShell>
  );
};

const LandingPageWrapper: React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [initialFooterPage, setInitialFooterPage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const page = getInitialPage();
    if (page === 'support') {
      navigate('/support', { replace: true });
      return;
    }
    if (page === 'privacy') {
      window.location.replace('/privacy/');
      return;
    }
    if (page === 'terms') {
      window.location.replace('/terms/');
      return;
    }
    if (page === 'licenses') {
      setInitialFooterPage('Licenses');
    }
  }, [navigate]);

  const handleLogin = () => {
    navigate('/dashboard');
  };

  return (
    <div ref={scrollContainerRef} className="h-screen w-full overflow-y-auto overflow-x-hidden bg-black">
      <LandingPage
        onLogin={handleLogin}
        scrollContainerRef={scrollContainerRef}
        initialFooterPage={initialFooterPage}
      />
      <CookieConsentBanner />
    </div>
  );
};

const AttributionCapture: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    captureAttributionFromUrl(location.search);
  }, [location.search]);

  return null;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <BrowserRouter>
      <AttributionCapture />
      <AppEmailVerificationGate>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/oauth/connect" element={<McpConnectPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/privacy" element={<PrivacyRoutePage />} />
          <Route path="/terms" element={<TermsRoutePage />} />
          <Route path="/" element={<LandingPageWrapper />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<DashboardRoute />} />
              <Route path="/create" element={
                <Suspense fallback={<ViewLoader />}>
                  <CreateView />
                </Suspense>
              } />
              <Route path="/library" element={
                <Suspense fallback={<ViewLoader />}>
                  <LibraryView />
                </Suspense>
              } />
              <Route path="/billing" element={
                <Suspense fallback={<ViewLoader />}>
                  <BillingView />
                </Suspense>
              } />
              <Route path="/settings" element={
                <Suspense fallback={<ViewLoader />}>
                  <SettingsView />
                </Suspense>
              } />
              <Route path="/profile" element={
                <Suspense fallback={<ViewLoader />}>
                  <ProfileView />
                </Suspense>
              } />
              <Route path="/editor" element={
                <Suspense fallback={<ViewLoader />}>
                  <EditorView />
                </Suspense>
              } />
              <Route path="/workflows" element={
                <Suspense fallback={<ViewLoader />}>
                  <WorkflowsView />
                </Suspense>
              } />
              <Route path="/usage" element={
                <Suspense fallback={<ViewLoader />}>
                  <UsageView />
                </Suspense>
              } />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppEmailVerificationGate>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
